// Responsibilities:
// - Persist and restore textarea notes per guide option
// - Start-flow handling (GuidePage)
// - Option selection UI and storage (GuideOptionPage)
// - Overview page text substitution and ZIP download (GuideOverviewPage)
// - End/Question page previous link handling and Word/PDF download (GuideEndPage / GuideQuestionPage)

(function () {
    // --- Utilities ---
    const safeSession = {
        get(key) {
            try {
                return sessionStorage.getItem(key);
            } catch (e) {
                console.error('sessionStorage.get error', e);
                return null;
            }
        }, set(key, value) {
            try {
                sessionStorage.setItem(key, value);
            } catch (e) {
                console.error('sessionStorage.set error', e);
            }
        }, remove(key) {
            try {
                sessionStorage.removeItem(key);
            } catch (e) {
                console.error('sessionStorage.remove error', e);
            }
        }
    };

    function getSelectedOption() {
        return {
            id: safeSession.get('selectedOptionId'),
            title: safeSession.get('selectedOptionTitle'),
            url: safeSession.get('selectedOptionUrl'),
            filename: safeSession.get('selectedOptionFilename'),
        };
    }

    function getSessionStorageItemsByGuideId(guideId) {
        const result = {};
        if (!guideId) return result;
        const prefix = String(guideId) + '_';
        try {
            for (let i = 0; i < sessionStorage.length; i++) {
                const key = sessionStorage.key(i);
                if (key && key.startsWith(prefix)) {
                    result[key] = sessionStorage.getItem(key);
                }
            }
        } catch (e) {
            console.error('Error reading sessionStorage items by guideId', e);
        }
        return result;
    }

    // Generic fetch -> download helper. Returns a Promise.
    function fetchAndDownload(endpoint, data, defaultFilename, onComplete) {
        return fetch(endpoint, {
            method: 'POST', headers: {'Content-Type': 'application/json'}, body: JSON.stringify(data)
        })
            .then(response => {
                if (!response.ok) throw new Error('Network response was not ok: ' + response.status);
                return response.blob();
            })
            .then(blob => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = (data && data.filename) || defaultFilename;
                // Append to document to make click() work consistently across browsers
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
                if (typeof onComplete === 'function') onComplete();
            })
            .catch(err => {
                console.error('Error downloading file from ' + endpoint, err);
                if (typeof onComplete === 'function') onComplete(err);
            });
    }

    // Specific API wrappers
    const api = {
        downloadWord(data) {
            return fetchAndDownload('/umbraco/api/guide/DownloadWord', data, 'APV-handlingsplan.docx');
        }, downloadZip(data, onComplete) {
            return fetchAndDownload('/umbraco/api/guide/DownloadZip', data, 'APV-materiale.zip', onComplete);
        }
    };

    // --- Download orchestration ---
    // Chooses ZIP on overview page, otherwise always Word
    function sendItems() {
        const overview = document.querySelector('.guide-page-overview');
        const question = document.querySelector('.guide-page-question');
        const endPage = document.querySelector('.guide-page-end');
        const {id: selectedId, filename} = getSelectedOption();

        // Fall back to the data-guide-id attribute when no option is selected in session
        const fallbackGuideId = (overview || question || endPage)?.getAttribute('data-guide-id');
        const guideId = selectedId || fallbackGuideId;

        if (!guideId) {
            console.warn('No guideId available for download.');
            return;
        }

        const items = guideId ? getSessionStorageItemsByGuideId(guideId) : {};
        const data = {guideId: guideId, items};

        if (overview) {
            const downloadLink = document.getElementById('selectedOptionDownload');
            if (downloadLink) downloadLink.classList.add('loading');
            return api.downloadZip(data, () => {
                if (downloadLink) downloadLink.classList.remove('loading');
            });
        }


        // Only set filename if Word doc
        data.filename = filename;
        
        return api.downloadWord(data);
    }

    // --- Textarea persistence ---
    function initTextareas() {
        const textareas = document.querySelectorAll('textarea[id][data-guide-id]');
        if (!textareas || textareas.length === 0) return;

        textareas.forEach(textarea => {
            try {
                const guideId = textarea.getAttribute('data-guide-id');
                if (!guideId) return;
                const storageKey = `${guideId}_${textarea.id}`;

                const saved = safeSession.get(storageKey);
                if (saved !== null) textarea.value = saved;

                textarea.addEventListener('input', () => safeSession.set(storageKey, textarea.value));
            } catch (err) {
                console.error('Error initializing textarea storage', err);
            }
        });
    }

    // --- Guide page helpers ---
    function initStartFlow() {
        const yesButton = document.getElementById('yes');
        if (!yesButton) return;
        const guideId = yesButton.getAttribute('data-guide-id');
        if (guideId) safeSession.set('selectedOptionId', guideId);
    }

    function updateStartButtons(href) {
        document.querySelectorAll('.start-button').forEach(startBtn => {
            startBtn.disabled = false;
            startBtn.classList.remove('disabled');
            if (!startBtn.href || startBtn.href === '' || startBtn.dataset.empty === 'true') {
                startBtn.href = href;
                startBtn.dataset.empty = 'false';
            }
        });
    }

    function initOptionPage() {
        const saved = getSelectedOption();
        if (saved.id) {
            const radio = document.getElementById(saved.id);
            if (radio) {
                radio.checked = true;
                updateStartButtons(saved.url || radio.value);
            }
        }

        document.querySelectorAll('input[name="option"]').forEach(radio => {
            radio.addEventListener('change', () => {
                const li = radio.closest('li');
                const optionTitleEl = li ? li.querySelector('h3') : null;
                const optionTitle = optionTitleEl ? optionTitleEl.textContent.trim() : '';

                updateStartButtons(radio.value);

                safeSession.set('selectedOptionId', radio.id);
                safeSession.set('selectedOptionUrl', radio.value);
                safeSession.set('selectedOptionTitle', optionTitle);

                if (window.innerWidth < 993) {
                    const mobileBtn = document.querySelector('.start-button[data-mobile-btn]');
                    if (mobileBtn) mobileBtn.scrollIntoView({behavior: 'smooth', block: 'center'});
                }
            });
        });

        // Prevent clicks on disabled start buttons
        document.querySelectorAll('.start-button').forEach(startBtn => {
            startBtn.addEventListener('click', (e) => {
                if (startBtn.disabled || startBtn.classList.contains('disabled')) {
                    e.preventDefault();
                    e.stopPropagation();
                    return false;
                }
            });
        });
    }

    function initOverviewPage() {
        
        const saved = getSelectedOption();
        const hasSelectedOption = saved.id != null && saved.title != null && saved.url != null;

        if (!hasSelectedOption) {
            // Check if the page has a download link that can work without selected options
            const downloadLink = document.getElementById('selectedOptionDownload');
            if (!downloadLink) {
                // No download link and no selected option — clear storage and redirect
                safeSession.remove('selectedOptionId');
                safeSession.remove('selectedOptionTitle');
                safeSession.remove('selectedOptionUrl');
                safeSession.remove('selectedOptionFilename');
                console.warn('Missing session storage items, redirecting to option page.');
                redirectToOptionPage();
                return;
            }
        }

        if (saved.title) {
            const titleEl = document.getElementById('selectedOptionTitle');
            if (titleEl) {
                titleEl.textContent = titleEl.textContent.replaceAll('{navn}', saved.title.toLowerCase());
            }

            if (document.querySelector('.guide-page-overview')) {
                const selectedOptionDownload = document.getElementById('selectedOptionDownload');
                if (selectedOptionDownload) {
                    selectedOptionDownload.textContent = selectedOptionDownload.textContent.replaceAll('{navn}', saved.title.toLowerCase());
                }
            }
        }

        if (saved.url) {
            const startLink = document.getElementById('startSamtaleLink');
            if (startLink) startLink.href = saved.url;
        }
        
        const downloadLink = document.getElementById('selectedOptionDownload');
        if (downloadLink) {
            const filenameAttribute = downloadLink.getAttribute('data-filename');
            if (filenameAttribute) {
                safeSession.set('selectedOptionFilename', filenameAttribute);
            } else {
                safeSession.remove('selectedOptionFilename');
            }
            
            downloadLink.addEventListener('click', (event) => {
                event.preventDefault();
                // overview's sendItems should trigger ZIP download
                sendItems();
            });
        }

        function redirectToOptionPage() {
            const optionPage = document.querySelector('.guide-page-overview .buttons-wrapper .back');
            if (optionPage && optionPage.href) {
                window.location.href = optionPage.href;
            }
        }
    }

    function initQuestionPage() {
        if (document.querySelector('.guide-page-question')) {
            safeSession.set('prevPageUrl', window.location.href);
        }
    }

    function initEndPage() {
        const prevPageLink = safeSession.get('prevPageUrl');
        const prevPageButton = document.getElementById('prevPageButton');
        if (prevPageLink && prevPageButton) prevPageButton.href = prevPageLink;
    }

    // Initialize all pieces; each init will only act on the relevant pages
    document.addEventListener('DOMContentLoaded', function () {
        initTextareas();
        initStartFlow();
        initOptionPage();
        initOverviewPage();
        initQuestionPage();
        initEndPage();
    });
})();
